import { useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  MOCK_CUSTOMERS,
  MOCK_VENDORS,
  MOCK_PRODUCTS,
  MOCK_PROCUREMENT,
  MOCK_SALES,
  MOCK_PURCHASES,
  MOCK_WAREHOUSES,
  MOCK_TRANSPORT,
  MOCK_LABOUR,
  MOCK_STAFF,
  MOCK_ROLES,
  MOCK_TASKS,
  MOCK_CATEGORIES,
  MOCK_UNITS,
  MOCK_CHAMBERS,
  MOCK_FLOORS,
  MOCK_RACKS,
  MOCK_INVENTORY_TRANSFERS,
  MOCK_DASHBOARD_STATS,
  DEMO_ORGS,
  MOCK_APPROVAL_DEFINITIONS,
  buildInitPayload,
  DEMO_OTP,
} from './mockData';
import type { Customer, Product, ProcRow, Vendor } from '../api/types';
import type { RootState } from '../store';
import { membershipIdFromJwt } from '../store/authSlice';

type PageArgs = {
  page?: number;
  pageLimit?: number;
  search?: string;
  listType?: string;
  category?: string;
  chamberId?: string;
  floorId?: string;
  customerIds?: string[];
  vendorIds?: string[];
  productIds?: string[];
};

type QueryOpts = { skip?: boolean; pollingInterval?: number };

function matchesSearch(haystack: string, search?: string) {
  if (!search?.trim()) return true;
  return haystack.toLowerCase().includes(search.trim().toLowerCase());
}

function paginate<T>(rows: T[], args?: PageArgs) {
  const page = args?.page ?? 1;
  const pageLimit = args?.pageLimit ?? 20;
  const start = (page - 1) * pageLimit;
  return {
    rows: rows.slice(start, start + pageLimit),
    total: rows.length,
    page,
    pageLimit,
  };
}

function queryResult<T>(data: T, opts?: QueryOpts) {
  if (opts?.skip) {
    return {
      data: undefined as T | undefined,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: undefined,
      fulfilledTimeStamp: 0,
      refetch: async () => ({ data }),
    };
  }
  return {
    data,
    isLoading: false,
    isFetching: false,
    isError: false,
    error: undefined,
    // Stable timestamp — Date.now() every render breaks consumers that depend on it
    fulfilledTimeStamp: 1,
    refetch: async () => ({ data }),
  };
}

function withUnwrap<T>(work: () => Promise<T> | T) {
  const dataPromise = Promise.resolve().then(() => work());
  const result = dataPromise.then((data) => ({ data })) as Promise<{ data: T }> & {
    unwrap: () => Promise<T>;
  };
  result.unwrap = () => dataPromise;
  return result;
}

function mutationTuple<T = unknown>(impl?: (arg: unknown) => Promise<T> | T) {
  const implRef = useRef(impl);
  implRef.current = impl;
  const trigger = useCallback(
    (arg?: unknown) => withUnwrap(() => (implRef.current ? implRef.current(arg) : ({} as T))),
    [],
  );
  return [trigger, { isLoading: false, error: undefined }] as const;
}

function lazyQuery<T>(resolve: (arg: unknown) => T) {
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;
  const trigger = useCallback(
    (arg: unknown) => withUnwrap(() => resolveRef.current(arg)),
    [],
  );
  return [trigger] as const;
}

export function useListCustomersQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(
    () =>
      MOCK_CUSTOMERS.filter((c) =>
        matchesSearch(`${c.name} ${c.phone ?? ''} ${c.email ?? ''}`, args?.search),
      ),
    [args?.search],
  );
  return queryResult({ listCustomers: paginate(filtered, args) }, opts);
}

export function useGetCustomerQuery(id: string, opts?: QueryOpts) {
  const row = MOCK_CUSTOMERS.find((c) => c.id === id) ?? null;
  return queryResult({ getCustomer: row }, opts);
}

export function useLazyGetCustomerQuery() {
  return lazyQuery((id) => ({
    getCustomer: MOCK_CUSTOMERS.find((c) => c.id === String(id)) ?? null,
  }));
}

export function useLazyListCustomersQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    const filtered = MOCK_CUSTOMERS.filter((c) =>
      matchesSearch(`${c.name} ${c.phone ?? ''}`, a.search),
    );
    return { listCustomers: paginate(filtered, a) };
  });
}

export function useAddUpdateCustomerMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as Partial<Customer> & { id?: string };
    return {
      addUpdateCustomer: {
        id: payload.id || `c${Date.now()}`,
        name: payload.name || 'New customer',
        phone: payload.phone ?? null,
        email: payload.email ?? null,
        openingBalance: payload.openingBalance ?? 0,
        address: null,
      },
    };
  });
}

export function useDeleteCustomerMutation() {
  return mutationTuple(() => ({ deleteCustomer: true }));
}

export function useListVendorsQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(
    () =>
      MOCK_VENDORS.filter((v) =>
        matchesSearch(`${v.name} ${v.phone ?? ''} ${v.email ?? ''}`, args?.search),
      ),
    [args?.search],
  );
  return queryResult({ listVendors: paginate(filtered, args) }, opts);
}

export function useGetVendorQuery(id: string, opts?: QueryOpts) {
  return queryResult(
    { getVendor: MOCK_VENDORS.find((v) => v.id === id) ?? null },
    opts,
  );
}

export function useLazyGetVendorQuery() {
  return lazyQuery((id) => ({
    getVendor: MOCK_VENDORS.find((v) => v.id === String(id)) ?? null,
  }));
}

export function useLazyListVendorsQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    const filtered = MOCK_VENDORS.filter((v) =>
      matchesSearch(`${v.name} ${v.phone ?? ''}`, a.search),
    );
    return { listVendors: paginate(filtered, a) };
  });
}

export function useAddUpdateVendorMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as Partial<Vendor> & { id?: string };
    return {
      addUpdateVendor: {
        id: payload.id || `v${Date.now()}`,
        name: payload.name || 'New vendor',
        phone: payload.phone ?? null,
        email: payload.email ?? null,
        openingBalance: payload.openingBalance ?? 0,
        address: null,
      },
    };
  });
}

export function useDeleteVendorMutation() {
  return mutationTuple(() => ({ deleteVendor: true }));
}

export function useListProductsQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(
    () =>
      MOCK_PRODUCTS.filter((p) =>
        matchesSearch(`${p.name} ${p.unit ?? ''} ${p.category?.name ?? ''}`, args?.search),
      ),
    [args?.search],
  );
  return queryResult({ listProducts: paginate(filtered, args) }, opts);
}

export function useGetProductQuery(id: string, opts?: QueryOpts) {
  const data = useMemo(
    () => ({ getProduct: MOCK_PRODUCTS.find((p) => p.id === id) ?? null }),
    [id],
  );
  return queryResult(data, opts);
}

export function useLazyGetProductQuery() {
  return lazyQuery((id) => ({
    getProduct: MOCK_PRODUCTS.find((p) => p.id === String(id)) ?? null,
  }));
}

export function useLazyListProductsQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    const filtered = MOCK_PRODUCTS.filter((p) =>
      matchesSearch(`${p.name} ${p.unit ?? ''}`, a.search),
    );
    return { listProducts: paginate(filtered, a) };
  });
}

export function useAddUpdateProductMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as Partial<Product> & { id?: string };
    return {
      addUpdateProduct: {
        id: payload.id || `p${Date.now()}`,
        name: payload.name || 'New product',
        unit: payload.unit ?? 'Piece',
        categoryId: payload.categoryId ?? null,
        category: payload.category ?? null,
        description: payload.description ?? null,
        quantity: payload.quantity ?? 0,
        pricePerQuantity: payload.pricePerQuantity ?? 0,
        showSubse: payload.showSubse ?? true,
        addOnItem: payload.addOnItem ?? false,
        addOnItemName: payload.addOnItemName ?? null,
        imageFileId: payload.imageFileId ?? null,
        imageUrl: payload.imageUrl ?? null,
      },
    };
  });
}

export function useDeleteProductMutation() {
  return mutationTuple(() => ({ deleteProduct: true }));
}

export function useLazyListCategoriesQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    const filtered = MOCK_CATEGORIES.filter((c) => matchesSearch(c.name, a.search));
    return { listCategories: paginate(filtered, a) };
  });
}

export function useAddUpdateCategoryMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as { name?: string; id?: string };
    return {
      addUpdateCategory: {
        id: payload.id || `cat${Date.now()}`,
        name: payload.name || 'New category',
      },
    };
  });
}

export function useLazyListUnitsQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    const filtered = MOCK_UNITS.filter((u) => matchesSearch(u.name, a.search));
    return { listUnits: paginate(filtered, a) };
  });
}

export function useAddUpdateUnitMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as { name?: string; id?: string };
    return {
      addUpdateUnit: {
        id: payload.id || `u${Date.now()}`,
        name: payload.name || 'New unit',
      },
    };
  });
}

export function useListProcurementQuery(args?: PageArgs, opts?: QueryOpts) {
  const rows = useMemo(() => {
    let list: ProcRow[] = MOCK_PROCUREMENT;
    if (args?.listType === 'SALES') list = MOCK_SALES;
    if (args?.listType === 'PURCHASE') list = MOCK_PURCHASES;
    return list.filter((r) =>
      matchesSearch(
        `${r.ordNumber ?? ''} ${r.customer?.name ?? ''} ${r.vendor?.name ?? ''}`,
        args?.search,
      ),
    );
  }, [args?.listType, args?.search]);
  return queryResult({ listProcurement: paginate(rows, args) }, opts);
}

export function useGetProcurementQuery(id: string, opts?: QueryOpts) {
  return queryResult(
    { getProcurement: MOCK_PROCUREMENT.find((p) => p.id === id) ?? null },
    opts,
  );
}

export function useAddUpdateProcurementMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as Partial<ProcRow> & { id?: string };
    return {
      addUpdateProcurement: {
        id: payload.id || `proc${Date.now()}`,
        procType: payload.procType || 'SALES',
        ordNumber: payload.ordNumber || `SO-${Date.now()}`,
        procAmount: payload.procAmount ?? 0,
      },
    };
  });
}

export function useExportProcurementMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as { listType?: string; format?: 'EXCEL' | 'TALLY' };
    const format = payload.format === 'TALLY' ? 'TALLY' : 'EXCEL';
    return {
      exportProcurement: {
        jobId: `demo-export-${format.toLowerCase()}-${Date.now()}`,
      },
    };
  });
}

export function useApproveSalesOrderMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as {
      procId?: string;
      decision?: 'APPROVED' | 'REJECTED';
    };
    return {
      approveSalesOrder: {
        id: payload.procId || 's1',
        approvalStatus: payload.decision || 'APPROVED',
      },
    };
  });
}

export function useDashboardStatsQuery(_arg?: undefined, opts?: QueryOpts) {
  return queryResult(MOCK_DASHBOARD_STATS, opts);
}

export function useListStaffQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(
    () =>
      MOCK_STAFF.filter((s) =>
        matchesSearch(
          `${s.user?.name ?? ''} ${s.user?.phone ?? ''} ${s.role?.name ?? ''}`,
          args?.search,
        ),
      ),
    [args?.search],
  );
  return queryResult({ listStaff: paginate(filtered, args) }, opts);
}

export function useAddUpdateStaffMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as Record<string, unknown>;
    return {
      addUpdateStaff: {
        id: String(payload.id || `st${Date.now()}`),
        userId: String(payload.userId || `u${Date.now()}`),
        roleId: payload.roleId ? String(payload.roleId) : null,
        isActive: true,
        user: {
          name: String(payload.name || 'Staff'),
          phone: String(payload.phone || ''),
          email: payload.email ? String(payload.email) : null,
        },
      },
    };
  });
}

export function useDeleteStaffMutation() {
  return mutationTuple(() => ({ deleteStaff: true }));
}

export function useListRolesQuery(_arg?: undefined, opts?: QueryOpts) {
  return queryResult({ listRoles: MOCK_ROLES }, opts);
}

export function useListFeatureGroupsQuery(_arg?: undefined, opts?: QueryOpts) {
  return queryResult({
    listFeatureGroups: [
      { module: 'customer', label: 'Customer', keys: ['customer.view','customer.create','customer.update','customer.delete','customer.view_all','customer.approve'] },
      { module: 'vendor', label: 'Vendor', keys: ['vendor.view','vendor.create','vendor.update','vendor.delete','vendor.view_all','vendor.approve'] },
      { module: 'product', label: 'Product', keys: ['product.view','product.create','product.update','product.delete','product.view_all','product.approve'] },
      { module: 'warehouse', label: 'Warehouse', keys: ['warehouse.view','warehouse.create','warehouse.update','warehouse.delete','warehouse.view_all','warehouse.approve'] },
      { module: 'procurement.sales', label: 'Procurement · Sales', keys: ['procurement.sales.view','procurement.sales.create','procurement.sales.update','procurement.sales.delete','procurement.sales.view_all','procurement.sales.approve'] },
      { module: 'procurement.purchase', label: 'Procurement · Purchase', keys: ['procurement.purchase.view','procurement.purchase.create','procurement.purchase.update','procurement.purchase.delete','procurement.purchase.view_all','procurement.purchase.approve'] },
      { module: 'roles', label: 'Roles', keys: ['roles.view','roles.create','roles.update','roles.delete'] },
      { module: 'staff', label: 'Staff', keys: ['staff.view','staff.create','staff.update','staff.delete','staff.view_all'] },
      { module: 'inventory', label: 'Inventory', keys: ['inventory.view','inventory.create','inventory.update','inventory.delete','inventory.view_all','inventory.confirm'] },
    ],
  }, opts);
}

export function useMyTasksQuery(args?: { category?: string }, opts?: QueryOpts) {
  const tasks = useMemo(() => {
    if (!args?.category || args.category === 'all') return MOCK_TASKS;
    return MOCK_TASKS.filter((t) => t.category === args.category);
  }, [args?.category]);
  return queryResult({ myTasks: tasks }, opts);
}

export function useCompleteTaskMutation() {
  return mutationTuple(() => ({ completeTask: { id: 'tk1', status: 'COMPLETED' } }));
}

export function useResolveTaskMutation() {
  return mutationTuple(() => ({ resolveTask: { id: 'tk1', status: 'RESOLVED' } }));
}

export function useListOrgSettingsQuery(_arg?: undefined, opts?: QueryOpts) {
  return queryResult({ listOrgSettings: [] }, opts);
}

export function useUpdateOrgLogoMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as { fileId?: string | null; logoUrl?: string };
    return {
      updateOrgLogo: {
        logoUrl: payload.fileId === null ? null : payload.logoUrl || '/brand/logo-sm.svg',
      },
    };
  });
}

const MOCK_ORG_BILLING = {
  planName: 'VIPSo Annual — 5 Users',
  planSlug: 'vipso-annual',
  status: 'active',
  billingPeriod: 'yearly',
  validFrom: '2026-08-04T00:00:00.000Z',
  validUntil: '2027-08-04T00:00:00.000Z',
  seatsUsed: 2,
  seatsAllowed: 5,
  seatCeiling: 15,
  extraSeatMonthly: 556,
  extraSeatYearly: 6000,
  operationsLocked: false,
  paymentDueAt: null as string | null,
  activeAddOns: [] as string[],
  addOnCatalogue: [
    {
      key: 'entity.approvals',
      name: 'Entity Approvals',
      description: 'Approval flows on Customer, Vendor, Product, Warehouse',
      meter: 'flat',
      monthlyPrice: 600,
      yearlyPrice: 6480,
      isActive: true,
      isSubscribed: false,
    },
    {
      key: 'org.seats',
      name: 'User Seats',
      description: 'Extra user seats',
      meter: 'per_user',
      monthlyPrice: 556,
      yearlyPrice: 6000,
      isActive: true,
      isSubscribed: false,
    },
  ],
};

const MOCK_ORG_INVOICES = {
  myOrgInvoices: [
    {
      id: 'inv1',
      invoiceNumber: 'INV-2026-0001',
      status: 'paid',
      subtotal: 30000,
      total: 30000,
      taxPct: 18,
      taxTotal: 5400,
      grandTotal: 35400,
      currency: 'INR',
      issuedAt: '2026-08-04T00:00:00.000Z',
      dueAt: '2026-08-04T00:00:00.000Z',
      paidAt: '2026-08-04T00:00:00.000Z',
      pdfFileId: null as string | null,
    },
  ],
};

const MOCK_ORG_BILLING_RESULT = {
  myOrgBilling: MOCK_ORG_BILLING,
};

export function useMyOrgBillingQuery(_arg?: undefined, opts?: QueryOpts) {
  return queryResult(MOCK_ORG_BILLING_RESULT, opts);
}

export function useMyOrgInvoicesQuery(_arg?: undefined, opts?: QueryOpts) {
  return queryResult(MOCK_ORG_INVOICES, opts);
}

export function useUpsertOrgSettingMutation() {
  return mutationTuple(() => ({ upsertOrgSetting: true }));
}

export function useFileUrlQuery(_id: string, opts?: QueryOpts) {
  return queryResult({ fileUrl: '/brand/logo-sm.svg' }, opts);
}

export function useLazyFileUrlQuery() {
  return lazyQuery(() => ({ fileUrl: '/brand/logo-sm.svg' }));
}

export function useCustomerCommentsQuery(_id: string, opts?: QueryOpts) {
  return queryResult({ customerComments: [] }, opts);
}

export function useAddCustomerCommentMutation() {
  return mutationTuple(() => ({ addCustomerComment: { id: 'cm1' } }));
}

/* Auth stubs (pages use local logic, but keep exports for completeness) */
export function useLoginMutation() {
  return mutationTuple(() => ({ login: { ok: true } }));
}

export function useLoginWithPasswordMutation() {
  return mutationTuple(() => ({ loginWithPassword: { ok: true } }));
}

export function useVerifyOtpMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as { otp?: string; phone?: string };
    if (payload.otp !== DEMO_OTP) {
      throw { status: 400, data: { message: 'Invalid OTP' } };
    }
    return {
      verifyOtp: {
        user: {
          id: '501',
          name: `User ${(payload.phone || '').slice(-4) || 'Demo'}`,
          phone: payload.phone || '9999999999',
        },
        orgs: buildInitPayload('101', payload.phone).orgs,
      },
    };
  });
}

export function useInitAppMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as { userId?: string; orgId?: string };
    return { initApp: buildInitPayload(payload.orgId || '101') };
  });
}

export function useRefreshSessionMutation() {
  return mutationTuple(() => ({ refreshSession: buildInitPayload('101') }));
}

/* Generated resource hooks (warehouse / transport / labour) */
export function useListWarehousesQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(
    () =>
      MOCK_WAREHOUSES.filter((w) =>
        matchesSearch(`${w.name} ${w.phone ?? ''}`, args?.search),
      ),
    [args?.search],
  );
  return queryResult({ listWarehouses: paginate(filtered, args) }, opts);
}

export function useAddUpdateWarehouseMutation() {
  return mutationTuple((input) => {
    const payload = ((input as { input?: Record<string, unknown> })?.input ||
      input ||
      {}) as { id?: string; name?: string; phone?: string; email?: string };
    return {
      addUpdateWarehouse: {
        id: payload.id || `w${Date.now()}`,
        name: payload.name || 'Warehouse',
        phone: payload.phone ?? null,
        email: payload.email ?? null,
      },
    };
  });
}

export function useDeleteWarehouseMutation() {
  return mutationTuple(() => ({ deleteWarehouse: true }));
}

export function useLazyListWarehousesQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    const filtered = MOCK_WAREHOUSES.filter((w) =>
      matchesSearch(`${w.name} ${w.phone ?? ''}`, a.search),
    );
    return { listWarehouses: paginate(filtered, a) };
  });
}

export function useListTransportQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(
    () =>
      MOCK_TRANSPORT.filter((t) =>
        matchesSearch(`${t.name} ${t.phone ?? ''}`, args?.search),
      ),
    [args?.search],
  );
  return queryResult({ listTransport: paginate(filtered, args) }, opts);
}

export function useAddUpdateTransportMutation() {
  return mutationTuple((input) => {
    const payload = ((input as { input?: Record<string, unknown> })?.input ||
      input ||
      {}) as { id?: string; name?: string; phone?: string };
    return {
      addUpdateTransport: {
        id: payload.id || `t${Date.now()}`,
        name: payload.name || 'Transport',
        phone: payload.phone ?? null,
      },
    };
  });
}

export function useDeleteTransportMutation() {
  return mutationTuple(() => ({ deleteTransport: true }));
}

export function useLazyListTransportQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    const filtered = MOCK_TRANSPORT.filter((t) =>
      matchesSearch(`${t.name} ${t.phone ?? ''}`, a.search),
    );
    return { listTransport: paginate(filtered, a) };
  });
}

export function useListLabourQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(
    () =>
      MOCK_LABOUR.filter((l) =>
        matchesSearch(`${l.name} ${l.phone ?? ''}`, args?.search),
      ),
    [args?.search],
  );
  return queryResult({ listLabour: paginate(filtered, args) }, opts);
}

export function useAddUpdateLabourMutation() {
  return mutationTuple((input) => {
    const payload = ((input as { input?: Record<string, unknown> })?.input ||
      input ||
      {}) as { id?: string; name?: string; phone?: string };
    return {
      addUpdateLabour: {
        id: payload.id || `l${Date.now()}`,
        name: payload.name || 'Labour',
        phone: payload.phone ?? null,
      },
    };
  });
}

export function useDeleteLabourMutation() {
  return mutationTuple(() => ({ deleteLabour: true }));
}

export function useLazyListLabourQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    const filtered = MOCK_LABOUR.filter((l) =>
      matchesSearch(`${l.name} ${l.phone ?? ''}`, a.search),
    );
    return { listLabour: paginate(filtered, a) };
  });
}

export function useSystemNotificationsQuery(_arg?: undefined, opts?: QueryOpts) {
  return queryResult({ systemNotifications: [] }, opts);
}

/* Storage locations (chambers / floors / racks) */
export function useListChambersQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(
    () => MOCK_CHAMBERS.filter((c) => matchesSearch(c.name, args?.search)),
    [args?.search],
  );
  return queryResult({ listChambers: paginate(filtered, args) }, opts);
}

export function useAddUpdateChamberMutation() {
  return mutationTuple((input) => {
    const payload = ((input as { input?: Record<string, unknown> })?.input ||
      input ||
      {}) as { id?: string; name?: string };
    return {
      addUpdateChamber: {
        id: payload.id || `ch${Date.now()}`,
        name: payload.name || 'Chamber',
      },
    };
  });
}

export function useDeleteChamberMutation() {
  return mutationTuple(() => ({ deleteChamber: true }));
}

export function useListFloorsQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(() => {
    let list = MOCK_FLOORS;
    if (args?.chamberId) list = list.filter((f) => f.chamberId === args.chamberId);
    return list.filter((f) => matchesSearch(f.name, args?.search));
  }, [args?.chamberId, args?.search]);
  return queryResult({ listFloors: paginate(filtered, args) }, opts);
}

export function useLazyListFloorsQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    let list = MOCK_FLOORS;
    if (a.chamberId) list = list.filter((f) => f.chamberId === a.chamberId);
    list = list.filter((f) => matchesSearch(f.name, a.search));
    return { listFloors: paginate(list, a) };
  });
}

export function useAddUpdateFloorMutation() {
  return mutationTuple((input) => {
    const payload = ((input as { input?: Record<string, unknown> })?.input ||
      input ||
      {}) as { id?: string; name?: string; chamberId?: string };
    return {
      addUpdateFloor: {
        id: payload.id || `fl${Date.now()}`,
        name: payload.name || 'Floor',
        chamberId: payload.chamberId || 'ch1',
      },
    };
  });
}

export function useDeleteFloorMutation() {
  return mutationTuple(() => ({ deleteFloor: true }));
}

export function useListRacksQuery(args?: PageArgs, opts?: QueryOpts) {
  const filtered = useMemo(() => {
    let list = MOCK_RACKS;
    if (args?.floorId) list = list.filter((r) => r.floorId === args.floorId);
    return list.filter((r) => matchesSearch(r.name, args?.search));
  }, [args?.floorId, args?.search]);
  return queryResult({ listRacks: paginate(filtered, args) }, opts);
}

export function useLazyListRacksQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as PageArgs;
    let list = MOCK_RACKS;
    if (a.floorId) list = list.filter((r) => r.floorId === a.floorId);
    list = list.filter((r) => matchesSearch(r.name, a.search));
    return { listRacks: paginate(list, a) };
  });
}

export function useAddUpdateRackMutation() {
  return mutationTuple((input) => {
    const payload = ((input as { input?: Record<string, unknown> })?.input ||
      input ||
      {}) as { id?: string; name?: string; floorId?: string };
    return {
      addUpdateRack: {
        id: payload.id || `rk${Date.now()}`,
        name: payload.name || 'Rack',
        floorId: payload.floorId || 'fl1',
      },
    };
  });
}

export function useDeleteRackMutation() {
  return mutationTuple(() => ({ deleteRack: true }));
}

export function useGetProductStockAtRackQuery(
  args: { productId: string; rackId?: string | null },
  opts?: QueryOpts,
) {
  const product = MOCK_PRODUCTS.find((p) => p.id === args.productId);
  const base = Number(product?.quantity ?? 0);
  // Demo: rack-scoped stock is a fraction of product qty; all-locations uses full qty.
  const stock = args.rackId ? Math.max(0, Math.floor(base * 0.35)) : base;
  return queryResult({ getProductStockAtRack: stock }, opts);
}

export function useLazyGetProductStockAtRackQuery() {
  return lazyQuery((args) => {
    const a = (args || {}) as { productId?: string; rackId?: string | null };
    const product = MOCK_PRODUCTS.find((p) => p.id === a.productId);
    const base = Number(product?.quantity ?? 0);
    const stock = a.rackId ? Math.max(0, Math.floor(base * 0.35)) : base;
    return { getProductStockAtRack: stock };
  });
}

/* Inventory transfers */
export function useGetProductInventoryCountsQuery(
  _args: unknown,
  opts?: QueryOpts,
) {
  return queryResult(
    { getProductInventoryCounts: { inHouse: 250, purchasePool: 100, inApproval: 30 } },
    opts,
  );
}

export function useListInventoryTransfersQuery(args?: PageArgs, opts?: QueryOpts) {
  return queryResult(
    { listInventoryTransfers: paginate(MOCK_INVENTORY_TRANSFERS, args) },
    opts,
  );
}

export function useAddUpdateInventoryTransferMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as Record<string, unknown>;
    return {
      addUpdateInventoryTransfer: {
        ...payload,
        id: String(payload.id || `it${Date.now()}`),
        status: 'DRAFT',
        createdAt: new Date().toISOString(),
      },
    };
  });
}

export function useConfirmInventoryTransferMutation() {
  return mutationTuple((id) => ({
    confirmInventoryTransfer: { id: String(id), status: 'CONFIRMED' },
  }));
}

export function useDeleteInventoryTransferMutation() {
  return mutationTuple((_id) => ({ deleteInventoryTransfer: { ok: true } }));
}

/* Roles CRUD */
export function useAddUpdateRoleMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as Record<string, unknown>;
    return {
      addUpdateRole: {
        id: String(payload.id || `r${Date.now()}`),
        name: String(payload.name || 'New Role'),
      },
    };
  });
}

export function useDeleteRoleMutation() {
  return mutationTuple(() => ({ deleteRole: true }));
}

/** Subject keys whose demo approval step was already decided this session. */
const resolvedApprovalSubjects = new Set<string>();

function approvalSubjectKey(subjectType: string, subjectId: string) {
  return `${String(subjectType).toUpperCase()}:${subjectId}`;
}

/** Resolve session membership the same way as EntityApprovalPanel. */
function useDemoMembershipId(): string {
  const stored = useSelector((s: RootState) => s.auth.membershipId);
  const accessToken = useSelector((s: RootState) => s.auth.accessToken);
  const org = useSelector((s: RootState) => s.auth.org);
  const orgs = useSelector((s: RootState) => s.auth.orgs);

  if (stored) return String(stored);
  const fromJwt = membershipIdFromJwt(accessToken);
  if (fromJwt) return fromJwt;
  const orgId = String(org?.id ?? org?.orgId ?? '');
  const match = orgs.find((o) => String(o.orgId) === orgId);
  if (match?.membershipId != null) return String(match.membershipId);
  return DEMO_ORGS[0]!.membershipId;
}

function mockEntityApprovalStatus(
  subjectType: string,
  subjectId: string,
): string | null {
  const id = String(subjectId);
  switch (String(subjectType).toUpperCase()) {
    case 'CUSTOMER':
      return MOCK_CUSTOMERS.find((c) => c.id === id)?.approvalStatus ?? null;
    case 'VENDOR':
      return MOCK_VENDORS.find((v) => v.id === id)?.approvalStatus ?? null;
    case 'PRODUCT':
      return MOCK_PRODUCTS.find((p) => p.id === id)?.approvalStatus ?? null;
    case 'WAREHOUSE':
      return (
        (MOCK_WAREHOUSES.find((w) => w.id === id) as { approvalStatus?: string } | undefined)
          ?.approvalStatus ?? null
      );
    case 'PROC':
      return MOCK_PROCUREMENT.find((p) => p.id === id)?.approvalStatus ?? null;
    default:
      return null;
  }
}

function setMockEntityApprovalStatus(
  subjectType: string,
  subjectId: string,
  status: string,
) {
  const id = String(subjectId);
  const apply = (row: { approvalStatus?: string | null } | undefined) => {
    if (row) row.approvalStatus = status;
  };
  switch (String(subjectType).toUpperCase()) {
    case 'CUSTOMER':
      apply(MOCK_CUSTOMERS.find((c) => c.id === id));
      break;
    case 'VENDOR':
      apply(MOCK_VENDORS.find((v) => v.id === id));
      break;
    case 'PRODUCT':
      apply(MOCK_PRODUCTS.find((p) => p.id === id));
      break;
    case 'WAREHOUSE':
      apply(MOCK_WAREHOUSES.find((w) => w.id === id) as { approvalStatus?: string } | undefined);
      break;
    case 'PROC':
      apply(MOCK_PROCUREMENT.find((p) => p.id === id));
      break;
    default:
      break;
  }
}

/* Approval flows (detail-page approve/reject) */
export function useListApprovalFlowsQuery(
  args: { subjectType: string; subjectId: string },
  opts?: QueryOpts,
) {
  const membershipId = useDemoMembershipId();
  const entityStatus = (
    mockEntityApprovalStatus(args.subjectType, args.subjectId) || ''
  ).toUpperCase();
  const subjectKey = approvalSubjectKey(args.subjectType, args.subjectId);
  // Re-read Set membership each render after mutations (same session).
  const alreadyResolved = resolvedApprovalSubjects.has(subjectKey);

  // Only PENDING (approve/reject) and REJECTED (acknowledge) need an open flow.
  const flows = useMemo(() => {
    if (opts?.skip || alreadyResolved) return [];
    if (entityStatus !== 'PENDING' && entityStatus !== 'REJECTED') return [];

    const isAck = entityStatus === 'REJECTED';
    const flowId = `flow-${args.subjectType}-${args.subjectId}`;
    return [
      {
        id: flowId,
        orgId: String(
          DEMO_ORGS.find((o) => o.membershipId === membershipId)?.orgId ?? '101',
        ),
        subjectType: args.subjectType,
        subjectId: args.subjectId,
        templateCode: `${args.subjectType}_APPROVAL`,
        status: isAck ? 'REJECTED' : 'PENDING',
        createdById: '1',
        createdAt: new Date().toISOString(),
        steps: [
          {
            id: `step-${args.subjectId}-1`,
            approvalFlowId: flowId,
            stepOrder: 1,
            stepKind: isAck ? 'REJECTION_ACK' : 'APPROVAL',
            // Always assign to the logged-in membership (any demo org).
            assigneeMembershipId: membershipId,
            status: 'PENDING',
            decidedByUserId: null,
            decidedAt: null,
            note: null,
            taskInstanceId: null,
          },
        ],
      },
    ];
  }, [
    alreadyResolved,
    args.subjectId,
    args.subjectType,
    entityStatus,
    membershipId,
    opts?.skip,
  ]);

  return queryResult({ listApprovalFlows: flows }, opts);
}

export function useApproveEntityMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as Record<string, unknown>;
    const subjectType = String(payload.subjectType || '');
    const subjectId = String(payload.subjectId || '');
    const decision = String(payload.decision || 'APPROVED').toUpperCase();
    const current = (
      mockEntityApprovalStatus(subjectType, subjectId) || ''
    ).toUpperCase();
    // Acknowledge keeps REJECTED; otherwise apply the decision.
    const nextStatus =
      current === 'REJECTED' ? 'REJECTED' : decision === 'REJECTED' ? 'REJECTED' : 'APPROVED';
    if (subjectType && subjectId) {
      setMockEntityApprovalStatus(subjectType, subjectId, nextStatus);
      resolvedApprovalSubjects.add(approvalSubjectKey(subjectType, subjectId));
    }
    return {
      approveEntity: {
        ok: true,
        flowId: `flow-${subjectType}-${subjectId}`,
        status: nextStatus,
      },
    };
  });
}

export function useListApprovalFlowDefinitionsQuery(_arg?: void, opts?: QueryOpts) {
  return queryResult(
    { listApprovalFlowDefinitions: MOCK_APPROVAL_DEFINITIONS },
    opts,
  );
}

export function useUpsertApprovalFlowDefinitionMutation() {
  return mutationTuple((input) => {
    const payload = (input || {}) as {
      moduleKey?: string;
      isEnabled?: boolean;
      levels?: Array<{ levelOrder?: number; roleId?: string; minAmount?: number }>;
    };
    const moduleKey = String(payload.moduleKey || '');
    let def = MOCK_APPROVAL_DEFINITIONS.find((d) => d.moduleKey === moduleKey);
    if (!def) {
      def = {
        id: `ad-${moduleKey.toLowerCase()}`,
        orgId: '101',
        moduleKey,
        isEnabled: false,
        levels: [],
      };
      MOCK_APPROVAL_DEFINITIONS.push(def);
    }
    def.isEnabled = Boolean(payload.isEnabled);
    const levels = [...(payload.levels ?? [])].sort(
      (a, b) => Number(a.levelOrder ?? 0) - Number(b.levelOrder ?? 0),
    );
    def.levels = levels.map((l, idx) => {
      const roleId = String(l.roleId || '');
      const role = MOCK_ROLES.find((r) => r.id === roleId);
      return {
        id: `lvl-${moduleKey}-${idx + 1}`,
        levelOrder: idx + 1,
        roleId,
        roleName: role?.name ?? roleId,
        minAmount: Number(l.minAmount ?? 0) || 0,
      };
    });
    return { upsertApprovalFlowDefinition: { ...def, levels: [...def.levels] } };
  });
}
